import { navigatorClient } from "@/api";
import type { ApiResponse } from "@/types";

// F01 Agent Finder · F01-1 에이전트 상세 · 북마크/비교함 (backend /finder · /me)

export interface FinderTag {
  kind: "agent_type" | "deployment";
  label: string;
  definition: string | null;
  secondary?: string | null;
}

export interface FinderCard {
  id: string;
  name: string;
  supplier_name: string | null;
  logo_url: string | null;
  is_certified: boolean;
  // △(비회원 · 승인 대기 · 공급자): 이름 · 공급사 · 로고 · Certified만 와요 (화면 Blur)
  locked: boolean;
  summary?: string | null;
  tags?: FinderTag[];
  category?: string | null;
  metric?: { label: string; value: string } | null;
  is_conceptual?: boolean;
  detail_type?: "external" | "internal";
  is_bookmarked?: boolean;
  in_compare?: boolean;
  confidential?: boolean;
  similarity?: number | null;
  is_top_match?: boolean;
}

export type FinderFilterKey = "industry" | "industry_detail" | "value_chain";

export interface FinderInterpretation {
  type: "산업" | "업무" | "키워드";
  text: string;
  code: string | null;
  label: string;
  filter: Partial<Record<FinderFilterKey, string[]>> | null;
}

export interface FinderFacet {
  code: string;
  label: string;
  count: number;
  parent?: string;
}

export interface FinderFilters {
  industry: string[];
  industry_detail: string[];
  value_chain: string[];
  certified: boolean;
  free_trial: boolean;
  on_premise: boolean;
}

export type FinderSort = "relevance" | "latest";

// 결과 없음 화면의 "이 조건을 빼면 결과가 있어요": 걸린 조건 하나를 뺐을 때 남는 추천 개수 (kind = 해제할 필터, 토글은 code 없음)
export interface FinderRelaxSuggestion {
  kind: FinderFilterKey | "certified" | "free_trial" | "on_premise";
  code: string | null;
  label: string;
  count: number;
}

export interface FinderResult {
  query: string | null;
  mode: "search" | "browse";
  search_mode: "semantic" | "keyword_fallback" | null;
  empty_reason: "no_interpretation" | null;
  interpretation: FinderInterpretation[];
  sort: FinderSort;
  locked: boolean;
  auto_applied: boolean;
  // 자동 선택한 조건 때문에 결과가 0건이 돼 조건을 풀어 보여 준 경우(칩은 그대로, 필터는 풀림)
  auto_relaxed: boolean;
  // 추천이 0건일 때만 (회원): 조건을 하나씩 빼면 남는 개수. 없으면 빈 배열
  relax_suggestions: FinderRelaxSuggestion[];
  filters: FinderFilters;
  recommended: FinderCard[];
  recommended_total: number;
  similar: FinderCard[];
  similar_total: number;
  facets: {
    industry: FinderFacet[];
    industry_detail: FinderFacet[];
    value_chain: FinderFacet[];
    toggles: { certified: number; free_trial: number; on_premise: number };
  };
}

export interface FinderQuery extends FinderFilters {
  q: string | null;
  sort: FinderSort;
}

export const EMPTY_FILTERS: FinderFilters = {
  industry: [],
  industry_detail: [],
  value_chain: [],
  certified: false,
  free_trial: false,
  on_premise: false,
};

const LIST_KEYS: FinderFilterKey[] = ["industry", "industry_detail", "value_chain"];
const TOGGLE_KEYS = ["certified", "free_trial", "on_premise"] as const;

// URL ↔ 조건 (검색어 · 필터 상태 URL 반영, IA 53 · 55): ?q=…&industry=IND-02&industry_detail=IND-02-01&certified=1&sort=latest
export function queryFromParams(params: URLSearchParams): FinderQuery {
  const list = (key: string) => (params.get(key) ?? "").split(",").filter(Boolean);
  return {
    q: params.get("q")?.trim() || null,
    industry: list("industry"),
    industry_detail: list("industry_detail"),
    value_chain: list("value_chain"),
    certified: params.get("certified") === "1",
    free_trial: params.get("free_trial") === "1",
    on_premise: params.get("on_premise") === "1",
    sort: params.get("sort") === "latest" ? "latest" : "relevance",
  };
}

// 검색어도 필터도 토글도 없으면 초기 진입(검색 시작 가이드, slide 32 6-1 · Figma 546:4397)
export function hasFinderConditions(query: FinderQuery): boolean {
  return Boolean(query.q) || LIST_KEYS.some((key) => query[key].length > 0) || TOGGLE_KEYS.some((key) => query[key]);
}

// 초기 진입(검색어 · 필터 없음) 화면: guide = 검색 시작 가이드형(기본, 10/13 제안 "가이드형 유지"), list = 전체 에이전트 목록형(Figma 847:57694).
// 둘 중 무엇으로 갈지는 문서 담당 확정 전이라 일단 가이드형으로 고정해요(바꿀 때는 이 값만 "list"로 — 목록형 화면은 이미 구현돼 있어요).
export const FINDER_INITIAL_VIEW: "guide" | "list" = "guide";

export function paramsFromQuery(query: FinderQuery, extra: Record<string, string> = {}): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  for (const key of LIST_KEYS) if (query[key].length) params.set(key, query[key].join(","));
  for (const key of TOGGLE_KEYS) if (query[key]) params.set(key, "1");
  if (query.sort === "latest") params.set("sort", "latest");
  for (const [k, v] of Object.entries(extra)) params.set(k, v);
  return params;
}

export async function searchAgents(query: FinderQuery, options: { offset?: number; limit?: number; auto?: boolean; signal?: AbortSignal } = {}) {
  const params = paramsFromQuery(query);
  if (options.offset) params.set("offset", String(options.offset));
  if (options.limit) params.set("limit", String(options.limit));
  if (options.auto) params.set("auto", "true");
  const { data } = await navigatorClient.get<ApiResponse<FinderResult>>(`/finder/search?${params.toString()}`, { signal: options.signal });
  return data.data!;
}

export type NvRankChangeValue = number | "new";

export interface TrendingItem {
  rank: number;
  id: string;
  name: string;
  summary: string | null;
  change: NvRankChangeValue;
  detail_type: "external" | "internal";
}

export interface TrendingResult {
  period: "weekly" | "monthly";
  items: TrendingItem[];
  locked: boolean;
  based_on_activity: boolean;
}

export async function getTrending(period: "weekly" | "monthly", signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<TrendingResult>>("/finder/trending", { params: { period }, signal });
  return data.data!;
}

// ── 북마크 · 비교함 (가정 A9: 20 · 3) ──────────────────────────────────

export type MarkKind = "bookmarks" | "compare";

export interface MarkState {
  agent_id: string;
  active: boolean;
  count: number;
  limit: number;
}

export async function setMark(kind: MarkKind, agentId: string, on: boolean) {
  const url = `/me/${kind}/${agentId}`;
  const { data } = on ? await navigatorClient.put<ApiResponse<MarkState>>(url) : await navigatorClient.delete<ApiResponse<MarkState>>(url);
  return data.data!;
}

export async function listMarks(kind: MarkKind, signal?: AbortSignal) {
  const { data } = await navigatorClient.get<ApiResponse<{ items: (FinderCard & { added_at: string | null })[]; count: number; limit: number }>>(
    `/me/${kind}`,
    { signal },
  );
  return data.data!;
}

export type FinderEvent =
  | "inquiry_click"
  | "brochure_download"
  | "definition_view"
  | "definition_download"
  | "homepage_click"
  | "playground_click";

// 퍼널 이벤트는 실패해도 화면 흐름을 막지 않아요
export function recordEvent(eventType: FinderEvent, agentId?: string, meta?: Record<string, unknown>) {
  navigatorClient.post("/finder/events", { event_type: eventType, agent_id: agentId ?? null, meta: meta ?? null }).catch(() => undefined);
}
